import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Enable2fa.css';

// Mock data for 2FA setup
const mockSetupData = {
  name: 'Coinsher - mrlmurali006@gmail.com',
  secretKey: 'XGMYNBYEZ2XLGHVKDBDR4KPXY4RNDJAK'
};

const Enable2fa: React.FC = () => {
  const navigate = useNavigate();
  const [copiedField, setCopiedField] = useState<'name' | 'key' | null>(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const handleBack = () => {
    navigate('/profile-security');
  };

  const handleCopy = async (text: string, field: 'name' | 'key') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleContinue = () => {
    setShowVerifyModal(true);
  };

  const handleCloseVerifyModal = () => {
    setShowVerifyModal(false);
    setOtpCode('');
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits and limit to 6 characters
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtpCode(value);
  };

  const handleVerifySubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (otpCode.length !== 6) {
      return;
    }

    setIsVerifying(true);

    // Simulate API verification
    setTimeout(() => {
      setIsVerifying(false);
      setShowVerifyModal(false);
      setShowSuccessModal(true);
    }, 1000);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    navigate('/profile-security');
  };

  // Format OTP for display with spaces
  const formatOtpDisplay = (code: string) => {
    return code.split('').join(' ');
  };

  const isOtpValid = otpCode.length === 6;

  return (
    <div className="enable2fa-container">
      {/* Header */}
      <header className="enable2fa-header">
        <button className="enable2fa-back-btn" onClick={handleBack} aria-label="Go back">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="enable2fa-title">Setup 2FA</h1>
      </header>

      {/* Content */}
      <div className="enable2fa-content">
        {/* Main Card */}
        <div className="enable2fa-card">
          {/* QR Code */}
          <div className="enable2fa-qr-wrapper">
            <div className="enable2fa-qr-code">
              {/* Mock QR Code Pattern */}
              <svg width="208" height="208" viewBox="0 0 208 208" fill="none">
                <rect width="208" height="208" fill="white"/>
                {/* QR Code pattern - simplified representation */}
                <rect x="16" y="16" width="48" height="48" fill="black"/>
                <rect x="24" y="24" width="32" height="32" fill="white"/>
                <rect x="32" y="32" width="16" height="16" fill="black"/>

                <rect x="144" y="16" width="48" height="48" fill="black"/>
                <rect x="152" y="24" width="32" height="32" fill="white"/>
                <rect x="160" y="32" width="16" height="16" fill="black"/>

                <rect x="16" y="144" width="48" height="48" fill="black"/>
                <rect x="24" y="152" width="32" height="32" fill="white"/>
                <rect x="32" y="160" width="16" height="16" fill="black"/>

                {/* Data modules - random pattern */}
                <rect x="80" y="16" width="8" height="8" fill="black"/>
                <rect x="96" y="16" width="8" height="8" fill="black"/>
                <rect x="112" y="16" width="8" height="8" fill="black"/>
                <rect x="80" y="32" width="8" height="8" fill="black"/>
                <rect x="104" y="32" width="8" height="8" fill="black"/>
                <rect x="120" y="32" width="8" height="8" fill="black"/>
                <rect x="88" y="48" width="8" height="8" fill="black"/>
                <rect x="112" y="48" width="8" height="8" fill="black"/>

                <rect x="16" y="80" width="8" height="8" fill="black"/>
                <rect x="32" y="80" width="8" height="8" fill="black"/>
                <rect x="48" y="80" width="8" height="8" fill="black"/>
                <rect x="16" y="96" width="8" height="8" fill="black"/>
                <rect x="40" y="96" width="8" height="8" fill="black"/>
                <rect x="16" y="112" width="8" height="8" fill="black"/>
                <rect x="32" y="112" width="8" height="8" fill="black"/>
                <rect x="48" y="112" width="8" height="8" fill="black"/>

                <rect x="80" y="80" width="8" height="8" fill="black"/>
                <rect x="96" y="80" width="8" height="8" fill="black"/>
                <rect x="112" y="80" width="8" height="8" fill="black"/>
                <rect x="80" y="96" width="8" height="8" fill="black"/>
                <rect x="104" y="96" width="8" height="8" fill="black"/>
                <rect x="120" y="96" width="8" height="8" fill="black"/>
                <rect x="88" y="112" width="8" height="8" fill="black"/>
                <rect x="112" y="112" width="8" height="8" fill="black"/>

                <rect x="144" y="80" width="8" height="8" fill="black"/>
                <rect x="160" y="80" width="8" height="8" fill="black"/>
                <rect x="176" y="80" width="8" height="8" fill="black"/>
                <rect x="152" y="96" width="8" height="8" fill="black"/>
                <rect x="168" y="96" width="8" height="8" fill="black"/>
                <rect x="184" y="96" width="8" height="8" fill="black"/>
                <rect x="144" y="112" width="8" height="8" fill="black"/>
                <rect x="176" y="112" width="8" height="8" fill="black"/>

                <rect x="80" y="144" width="8" height="8" fill="black"/>
                <rect x="96" y="144" width="8" height="8" fill="black"/>
                <rect x="120" y="144" width="8" height="8" fill="black"/>
                <rect x="88" y="160" width="8" height="8" fill="black"/>
                <rect x="104" y="160" width="8" height="8" fill="black"/>
                <rect x="80" y="176" width="8" height="8" fill="black"/>
                <rect x="112" y="176" width="8" height="8" fill="black"/>
                <rect x="128" y="176" width="8" height="8" fill="black"/>

                <rect x="144" y="144" width="8" height="8" fill="black"/>
                <rect x="168" y="144" width="8" height="8" fill="black"/>
                <rect x="184" y="144" width="8" height="8" fill="black"/>
                <rect x="152" y="160" width="8" height="8" fill="black"/>
                <rect x="176" y="160" width="8" height="8" fill="black"/>
                <rect x="144" y="176" width="8" height="8" fill="black"/>
                <rect x="160" y="176" width="8" height="8" fill="black"/>
                <rect x="184" y="176" width="8" height="8" fill="black"/>
              </svg>
            </div>
          </div>

          {/* Instructions */}
          <p className="enable2fa-instructions">
            Please scan the QR Code with your authenticator app. If you cannot scan the code, add information below into your app manually.
          </p>

          {/* Name Field */}
          <div className="enable2fa-field">
            <label className="enable2fa-label">Name:</label>
            <div className="enable2fa-copy-field">
              <span className="enable2fa-copy-text">{mockSetupData.name}</span>
              <button
                className="enable2fa-copy-btn"
                onClick={() => handleCopy(mockSetupData.name, 'name')}
                aria-label="Copy name"
              >
                {copiedField === 'name' ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="5.33334" y="5.33334" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M10.6667 5.33334V4.00001C10.6667 3.26363 10.0697 2.66667 9.33334 2.66667H4.00001C3.26363 2.66667 2.66667 3.26363 2.66667 4.00001V9.33334C2.66667 10.0697 3.26363 10.6667 4.00001 10.6667H5.33334" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Key Field */}
          <div className="enable2fa-field">
            <label className="enable2fa-label">Key:</label>
            <div className="enable2fa-copy-field">
              <span className="enable2fa-copy-text enable2fa-copy-text-mono">{mockSetupData.secretKey}</span>
              <button
                className="enable2fa-copy-btn"
                onClick={() => handleCopy(mockSetupData.secretKey, 'key')}
                aria-label="Copy key"
              >
                {copiedField === 'key' ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="5.33334" y="5.33334" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M10.6667 5.33334V4.00001C10.6667 3.26363 10.0697 2.66667 9.33334 2.66667H4.00001C3.26363 2.66667 2.66667 3.26363 2.66667 4.00001V9.33334C2.66667 10.0697 3.26363 10.6667 4.00001 10.6667H5.33334" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Helper Text */}
          <p className="enable2fa-helper">
            Once you have successfully added to your authenticator app, press continue
          </p>
        </div>

        {/* Continue Button */}
        <button className="enable2fa-continue-btn" onClick={handleContinue}>
          Continue
        </button>
      </div>

      {/* Verification Modal */}
      {showVerifyModal && (
        <div className="enable2fa-verify-overlay" onClick={handleCloseVerifyModal}>
          <div className="enable2fa-verify-dialog" onClick={(e) => e.stopPropagation()}>
            {/* Title */}
            <h2 className="enable2fa-verify-title">Enable 2 Factor Authentication</h2>

            {/* Description */}
            <p className="enable2fa-verify-description">
              Please enter the 6 digit code sent to your Authenticator app
            </p>

            {/* OTP Input */}
            <form onSubmit={handleVerifySubmit}>
              <div className="enable2fa-verify-input-wrapper">
                <input
                  type="text"
                  className="enable2fa-verify-input"
                  value={formatOtpDisplay(otpCode)}
                  onChange={handleOtpChange}
                  placeholder="0 0 0 0 0 0"
                  maxLength={11}
                  autoFocus
                  inputMode="numeric"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className={`enable2fa-verify-btn ${!isOtpValid || isVerifying ? 'disabled' : ''}`}
                disabled={!isOtpValid || isVerifying}
              >
                {isVerifying ? 'Verifying...' : 'Enable 2FA'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="enable2fa-modal-overlay" onClick={handleCloseSuccessModal}>
          <div className="enable2fa-modal-dialog" onClick={(e) => e.stopPropagation()}>
            {/* Success Icon */}
            <div className="enable2fa-modal-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <path d="M40 14L18 36L8 26" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            {/* Title */}
            <h2 className="enable2fa-modal-title">2FA Enabled!</h2>

            {/* Message */}
            <p className="enable2fa-modal-message">
              Your account is now protected with two-factor authentication
            </p>

            {/* Close Button */}
            <button className="enable2fa-modal-btn" onClick={handleCloseSuccessModal}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Enable2fa;
